import { Http } from '@/lib/http';
import { serialize } from 'object-to-formdata';

export class CaseManagementService extends Http {
  constructor() {
    super();
    this.endpoint = 'delinquency-pro/app/case-management';
  }

  updateLoanDetails(id, loanDetails) {
    return this.httpClient().patch(`${this.endpoint}/cases/${id}/loan`, {
      ...loanDetails
    });
  }

  updateExecutiveSummaryReport(caseId, loanId, report) {
    return this.httpClient().patch(
      `${this.endpoint}/cases/${caseId}/loans/${loanId}/executive-summary`,
      { report }
    );
  }

  getLoanDetails(id) {
    return this.httpClient().get(`${this.endpoint}/loans/${id}`);
  }

  getExecutiveSummary(caseId, loanId) {
    return this.httpClient().get(
      `${this.endpoint}/cases/${caseId}/loans/${loanId}/executive-summary`
    );
  }

  getLoanCases(id) {
    return this.httpClient().get(`${this.endpoint}/loans/${id}/cases`);
  }

  getWorkloadCount(userId) {
    return this.httpClient().get(
      `${this.endpoint}/statistics/workload-count?${userId}`
    );
  }

  getCase(id) {
    return this.httpClient().get(`${this.endpoint}/cases/${id}`);
  }

  getCaseFile(id) {
    return this.httpClient().get(`${this.endpoint}/cases/details/${id}`);
  }

  reopenCase(caseId, userId) {
    return this.httpClient().post(
      `${this.endpoint}/cases/${caseId}/reopen/${userId}`
    );
  }

  assignCase(caseId, userId) {
    return this.httpClient().put(
      `${this.endpoint}/cases/${caseId}/assign/${userId}`
    );
  }

  unassignCase(caseId, reassignmentStatusId) {
    return this.httpClient().delete(
      `${this.endpoint}/cases/${caseId}/assign?reassignmentStatusId=${reassignmentStatusId}`
    );
  }

  closeCase(id, { closureReason, comment }) {
    return this.httpClient().post(`${this.endpoint}/cases/${id}/close`, {
      closureReason,
      comment
    });
  }

  escalateCase(caseId, { comment, escalateeId }) {
    return this.httpClient().patch(
      `${this.endpoint}/cases/${caseId}/escalate`,
      {
        comment,
        escalateeId
      }
    );
  }

  getDelinquencyActions(caseId) {
    return this.httpClient().get(
      `${this.endpoint}/cases/${caseId}/delinquency-actions`
    );
  }

  saveDelinquencyActionTemplate(caseId, actionId, templateDetails) {
    return this.httpClient().patch(
      `${this.endpoint}/cases/${caseId}/delinquency-actions/${actionId}/template`,
      templateDetails
    );
  }

  assignDelinquencyAction(caseId, delinquencyActionId, userId) {
    return this.httpClient().patch(
      `${this.endpoint}/cases/${caseId}/delinquency-actions/${delinquencyActionId}/users/${userId}`
    );
  }

  unassignDelinquencyAction(caseId, delinquencyActionId, userId) {
    return this.httpClient().delete(
      `${this.endpoint}/cases/${caseId}/delinquency-actions/${delinquencyActionId}/users/${userId}`
    );
  }

  updateDelinquencyActionStatus(
    caseId,
    delinquencyActionId,
    { status, comment, completedBy, graceDays },
    fileList
  ) {
    let formData = serialize(
      { status, comment, completedBy, graceDays },
      {
        indices: true,
        nullsAsUndefineds: true,
        allowEmptyArrays: false
      }
    );

    for (let i = 0; i < fileList?.length; i++) {
      formData.append(`files`, fileList[i]);
    }

    return this.httpClient({
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }).patch(
      `${this.endpoint}/cases/${caseId}/delinquency-actions/${delinquencyActionId}/status`,
      formData
    );
  }

  getOtherLoans(caseId) {
    return this.httpClient().get(`${this.endpoint}/cases/${caseId}/loans`);
  }

  getAssociatedCollaterals(caseId) {
    return this.httpClient().get(
      `${this.endpoint}/cases/${caseId}/collaterals`
    );
  }

  getExternalParticipants(caseId) {
    return this.httpClient().get(
      `${this.endpoint}/cases/${caseId}/external-participants`
    );
  }

  assignExternalParticipant(
    caseIdOrCollateralId,
    externalParticipantId,
    { dateAssigned }
  ) {
    return this.httpClient().put(
      `${this.endpoint}/cases/${caseIdOrCollateralId}/external-participants/${externalParticipantId}/`,
      { dateAssigned }
    );
  }

  unassignExternalParticipant(caseIdOrCollateralId, externalParticipantId) {
    return this.httpClient().delete(
      `${this.endpoint}/cases/${caseIdOrCollateralId}/external-participants/${externalParticipantId}/`
    );
  }

  createForeclosureCase(caseId, foreclosureCase) {
    return this.httpClient().post(
      `${this.endpoint}/cases/${caseId}/foreclosure`,
      foreclosureCase
    );
  }

  saveCollectorInformation(caseId, externalParticipantId, information) {
    return this.httpClient().put(
      `${this.endpoint}/cases/${caseId}/collectors/${externalParticipantId}`,
      {
        ...information
      }
    );
  }

  saveLegalRepresentativeInformation(
    caseId,
    externalParticipantId,
    information
  ) {
    return this.httpClient().put(
      `${this.endpoint}/cases/${caseId}/legal-representatives/${externalParticipantId}`,
      {
        ...information
      }
    );
  }

  saveBailiffInformation(collateralId, externalParticipantId, information) {
    return this.httpClient().put(
      `${this.endpoint}/collaterals/${collateralId}/bailiffs/${externalParticipantId}`,
      {
        ...information
      }
    );
  }

  getFieldVisit(caseId, fieldVisit) {
    return this.httpClient().get(
      `${this.endpoint}/cases/${caseId}/field-visits/${fieldVisit}`
    );
  }
  getAssignedActions() {
    return this.httpClient().get(`${this.endpoint}/assigned`);
  }
  getFieldVisits(caseId) {
    return this.httpClient().get(
      `${this.endpoint}/cases/${caseId}/field-visits`
    );
  }

  deleteFieldVisit(caseId, fieldVisitId) {
    return this.httpClient().delete(
      `${this.endpoint}/cases/${caseId}/field-visits/${fieldVisitId}`
    );
  }

  downloadFile(fileId) {
    return this.httpClient().get(`upload/file/${fileId}`);
  }

  saveFieldVisit(caseId, fieldVisits, fileList) {
    let formData = serialize(fieldVisits, {
      indices: true,
      nullsAsUndefineds: true,
      allowEmptyArrays: false
    });

    for (let i = 0; i < fileList?.length; i++) {
      formData.append(`files`, fileList[i], fileList[i].name);
    }

    return this.httpClient({
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }).post(`${this.endpoint}/cases/${caseId}/field-visits`, formData);
  }

  getComments(caseId, { search = '', page = 0, limit = 10 }) {
    return this.httpClient().get(
      `${this.endpoint}/cases/${caseId}/comments?search=${search}&page=${page}&limit=${limit}`
    );
  }

  createComment(caseId, commentDetails, fileList) {
    let formData = serialize(
      { ...commentDetails },
      {
        indices: true,
        nullsAsUndefineds: true,
        allowEmptyArrays: false
      }
    );

    for (let i = 0; i < fileList?.length; i++) {
      formData.append(`files`, fileList[i]);
    }

    return this.httpClient({
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }).post(`${this.endpoint}/cases/${caseId}/comments`, formData);
  }

  updateComment(caseId, commentId, commentDetails, fileList) {
    let formData = serialize(
      { ...commentDetails },
      {
        indices: true,
        nullsAsUndefineds: true,
        allowEmptyArrays: false
      }
    );

    for (let i = 0; i < fileList?.length; i++) {
      formData.append(`files`, fileList[i]);
    }

    return this.httpClient({
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }).patch(
      `${this.endpoint}/cases/${caseId}/comments/${commentId}`,
      formData
    );
  }

  deleteComment(caseId, commentId) {
    return this.httpClient().delete(
      `${this.endpoint}/cases/${caseId}/comments/${commentId}`
    );
  }
}
